import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Collapse,
  Empty,
  Input,
  Select,
  Space,
  Spin,
  Tag,
  Typography,
  message,
} from 'antd';
import {
  CheckOutlined,
  CopyOutlined,
  DownloadOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import MarketingInspirationPicker from './MarketingInspirationPicker';
import type { MarketingInspirationItem } from './types';
import type { MarketingSocialAsset, MarketingSocialDraft } from './marketingWorkspaceState';
import { composeSocialCards, serializeSocialAsset } from './socialContent';
import { sortMarketingInspirationItems } from './inspiration';

const { Paragraph, Title } = Typography;

interface MarketingSocialComposerProps {
  apiBase: string;
  bookId: string;
  userId: string;
  sourceLanguage: string;
  editionLanguage: string;
  bookTitle?: string;
  draft: MarketingSocialDraft;
  onDraftChange: (draft: MarketingSocialDraft) => void;
  onSaveAsset: (draft: MarketingSocialDraft) => MarketingSocialAsset;
  onOpenBookKit: () => void;
  sourceRefreshSignal?: number;
}

const FORMAT_LABELS = {
  story_slides: 'TikTok story slides',
  carousel: 'Carousel',
  quote_card: 'Quote card',
} as const;

const readError = async (response: Response, fallback: string): Promise<string> => {
  try {
    const payload = await response.json();
    return String(payload?.detail || payload?.message || fallback);
  } catch {
    return fallback;
  }
};

const MarketingSocialComposer: React.FC<MarketingSocialComposerProps> = ({
  apiBase,
  bookId,
  userId,
  sourceLanguage,
  editionLanguage,
  bookTitle = 'Book',
  draft,
  onDraftChange,
  onSaveAsset,
  onOpenBookKit,
  sourceRefreshSignal,
}) => {
  const [items, setItems] = useState<MarketingInspirationItem[]>([]);
  const [loading, setLoading] = useState(false);
  const lastSourceRefreshSignalRef = React.useRef(sourceRefreshSignal);

  const loadSources = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({ userId, sourceLanguage, editionLanguage });
      const response = await fetch(
        `${apiBase}/api/books/${encodeURIComponent(bookId)}/marketing/inspiration?${params.toString()}`
      );
      if (!response.ok) throw new Error(await readError(response, 'Failed to load Book Kit sources.'));
      const payload = await response.json();
      setItems(sortMarketingInspirationItems(Array.isArray(payload?.items) ? payload.items : []));
    } catch (error: any) {
      message.error(error?.message || 'Failed to load Book Kit sources.');
    } finally {
      setLoading(false);
    }
  }, [apiBase, bookId, editionLanguage, sourceLanguage, userId]);

  useEffect(() => {
    void loadSources();
  }, [loadSources]);

  useEffect(() => {
    if (sourceRefreshSignal === undefined) return;
    if (lastSourceRefreshSignalRef.current === sourceRefreshSignal) return;
    lastSourceRefreshSignalRef.current = sourceRefreshSignal;
    void loadSources();
  }, [loadSources, sourceRefreshSignal]);

  const approvedItems = useMemo(
    () => items.filter(item => String(item.status || '').toLowerCase() === 'approved'),
    [items]
  );

  const updateDraft = (patch: Partial<MarketingSocialDraft>) => {
    onDraftChange({ ...draft, ...patch, updatedAt: new Date().toISOString() });
  };

  const compose = () => {
    if (!draft.selectedSourceIds.length) {
      message.warning('Choose at least one approved Book Kit source first.');
      return;
    }
    updateDraft({ cards: composeSocialCards(draft, approvedItems, bookTitle) });
  };

  const updateCard = (cardId: string, text: string) => {
    updateDraft({ cards: draft.cards.map(card => card.id === cardId ? { ...card, text } : card) });
  };

  const copyPack = async () => {
    await navigator.clipboard.writeText(serializeSocialAsset(draft));
    message.success('Content pack copied.');
  };

  const downloadPack = () => {
    const blob = new Blob([serializeSocialAsset(draft)], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = `${draft.title.replace(/[^a-z0-9]+/gi, '-').replace(/^-|-$/g, '').toLowerCase() || 'social-content'}.txt`;
    anchor.click();
    URL.revokeObjectURL(url);
  };

  const saveAsset = () => {
    if (!draft.cards.length) {
      message.warning('Build and review the content pack before saving it.');
      return;
    }
    onSaveAsset(draft);
    message.success(`${FORMAT_LABELS[draft.format]} saved to Assets.`);
  };

  return (
    <Spin spinning={loading}>
      <div className="marketing-social-composer">
        <section className="marketing-creator-header">
          <div>
            <span className="marketing-workspace__kicker">Social content</span>
            <Title level={3}>{FORMAT_LABELS[draft.format]}</Title>
            <Paragraph>
              Split verified prose from the {editionLanguage} edition into an editable,
              platform-ready content pack. This step does not call an AI model.
            </Paragraph>
          </div>
          <Tag color="green">Draft saved on this device</Tag>
        </section>

        {!approvedItems.length ? (
          <Alert
            type="warning"
            showIcon
            message="Approve Book Kit sources first"
            description="Story slides and cards stay grounded in your manuscript by using approved scenes, exact quotes, and moods."
            action={<Button onClick={onOpenBookKit}>Open Book Kit</Button>}
          />
        ) : null}

        <section className="marketing-creator-section">
          <div className="marketing-creator-section__header">
            <div><span>1</span><div><strong>Sources</strong><p>Choose the manuscript material this asset may use.</p></div></div>
          </div>
          <MarketingInspirationPicker
            items={approvedItems}
            loading={loading}
            selectedIds={draft.selectedSourceIds}
            onChange={selectedSourceIds => updateDraft({
              selectedSourceIds: draft.format === 'story_slides'
                ? selectedSourceIds.slice(-1)
                : selectedSourceIds,
              cards: [],
            })}
            helperText={draft.format === 'story_slides'
              ? 'Choose one approved scene passage. Its exact edition prose will be split across the slides.'
              : 'Use approved scenes, quotes, and moods. Exact source text remains labeled in the finished pack.'}
            embedded
          />
          {!approvedItems.length ? <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No approved sources yet." /> : null}
        </section>

        <section className="marketing-creator-section">
          <div className="marketing-creator-section__header">
            <div><span>2</span><div><strong>Creative direction</strong><p>Set the audience-facing tone and ending.</p></div></div>
          </div>
          <div className="marketing-social-composer__direction-grid">
            <label>
              <span>Asset name</span>
              <Input value={draft.title} onChange={event => updateDraft({ title: event.target.value })} />
            </label>
            <label>
              <span>Tone</span>
              <Input value={draft.tone} onChange={event => updateDraft({ tone: event.target.value })} />
            </label>
            <label>
              <span>Call to action</span>
              <Input value={draft.callToAction} onChange={event => updateDraft({ callToAction: event.target.value })} />
            </label>
            <label>
              <span>Maximum spoiler level</span>
              <Select
                value={draft.spoilerLevel}
                onChange={spoilerLevel => updateDraft({ spoilerLevel, cards: [] })}
                options={[
                  { value: 'low', label: 'Low spoilers' },
                  { value: 'medium', label: 'Medium spoilers' },
                  { value: 'high', label: 'High spoilers' },
                ]}
              />
            </label>
          </div>
          <Collapse
            ghost
            items={[{
              key: 'advanced',
              label: 'Advanced direction',
              children: (
                <Space direction="vertical" size={10} style={{ width: '100%' }}>
                  <label className="marketing-social-composer__full-field">
                    <span>Caption</span>
                    <Input.TextArea rows={3} value={draft.caption} onChange={event => updateDraft({ caption: event.target.value })} />
                  </label>
                  <label className="marketing-social-composer__full-field">
                    <span>Campaign note</span>
                    <Input.TextArea rows={2} value={draft.campaignNote} onChange={event => updateDraft({ campaignNote: event.target.value })} />
                  </label>
                </Space>
              ),
            }]}
          />
          <Button
            type="primary"
            icon={<ReloadOutlined />}
            disabled={!draft.selectedSourceIds.length}
            onClick={compose}
          >
            {draft.cards.length ? 'Rebuild content pack' : 'Build content pack'}
          </Button>
        </section>

        <section className="marketing-creator-section">
          <div className="marketing-creator-section__header">
            <div><span>3</span><div><strong>Review & save</strong><p>Edit every card before it becomes an asset.</p></div></div>
          </div>
          {draft.cards.length ? (
            <div className={`marketing-social-preview marketing-social-preview--${draft.format}`}>
              {draft.cards.map((card, index) => (
                <article key={card.id} className="marketing-social-card">
                  <div className="marketing-social-card__meta">
                    <span>{draft.format === 'quote_card' ? 'Card' : `Slide ${index + 1}`}</span>
                    <Space size={4}>
                      <Tag>{card.role}</Tag>
                      {card.exactQuote ? <Tag color="gold">Exact quote</Tag> : null}
                      {card.exactSource && !card.exactQuote ? <Tag color="green">Exact passage</Tag> : null}
                    </Space>
                  </div>
                  <Input.TextArea
                    aria-label={`${draft.format === 'quote_card' ? 'Card' : `Slide ${index + 1}`} text`}
                    autoSize={{ minRows: 3, maxRows: 8 }}
                    value={card.text}
                    onChange={event => updateCard(card.id, event.target.value)}
                  />
                </article>
              ))}
            </div>
          ) : (
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Choose sources, then build a content pack to review it here." />
          )}
          <div className="marketing-social-composer__actions">
            <Button icon={<CopyOutlined />} disabled={!draft.cards.length} onClick={() => void copyPack()}>Copy pack</Button>
            <Button icon={<DownloadOutlined />} disabled={!draft.cards.length} onClick={downloadPack}>Download text</Button>
            <Button type="primary" icon={<CheckOutlined />} disabled={!draft.cards.length} onClick={saveAsset}>Save to Assets</Button>
          </div>
        </section>
      </div>
    </Spin>
  );
};

export default MarketingSocialComposer;
